/** @jsx jsx */
import React, { memo, useCallback, useState } from 'react';
import { jsx } from '@compiled/react';
import { Drawer, DrawerContent } from '@atlaskit/drawer';

import { Inline } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';

import { ErrorFlag, FlagGroup } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type { StageProps } from '../../../common/types.tsx';
import CreateSubpage from './create/index.tsx';
import ReviewSubpage from './review/index.tsx';
import ReciprocalPromptSubpage from './reciprocal-prompt/index.tsx';
import ConfirmationSubpage from './confirmation/index.tsx';
import ConnectSubpage from './connect/index.tsx';
import messages from './messages.tsx';

const DEFAULT_CREATE_STAGE: StageProps = { kind: 'create' };

type StateMachineProps = {
	state: StageProps;
	onCancel: () => void;
	onStateChange: (newState: StageProps) => void;
	onDisplayError: (err: string) => void;
};

const StateMachine = memo((props: StateMachineProps) => {
	switch (props.state.kind) {
		case 'create':
		case 'manual-create':
			return (
				<CreateSubpage
					onError={props.onDisplayError}
					onSuccess={props.onStateChange}
					onCancel={props.onCancel}
				/>
			);
		case 'review':
			return (
				<ReviewSubpage
					state={props.state}
					onSuccess={props.onStateChange}
					onError={props.onDisplayError}
					onCancel={props.onCancel}
				/>
			);
		case 'reciprocal-prompt':
			return <ReciprocalPromptSubpage state={props.state} />;
		case 'connect':
			return (
				<ConnectSubpage
					state={props.state}
					onSuccess={props.onCancel} // Success = close the drawer
					onError={props.onDisplayError}
					onCancel={props.onCancel}
				/>
			);
		case 'confirmation':
			return <ConfirmationSubpage state={props.state} />;
		case 'loading':
			props.state.promise.then((newState) => {
				props.onStateChange(newState);
			});

			return (
				<Inline alignBlock="center" alignInline="center">
					<Spinner />
				</Inline>
			);
		default:
			return <></>;
	}
});

export type CreateFlowDialogProps = {
	isOpen: boolean;
	onExit: () => void;
	onCloseComplete?: () => void;
	startingStage?: StageProps;
};

export const CreateFlowDialog = (props: CreateFlowDialogProps): React.JSX.Element => {
	// Use the provided starting stage or default to the create stage
	const [flowState, setFlowState] = useState<StageProps>(
		props.startingStage || DEFAULT_CREATE_STAGE,
	);

	const { formatMessage } = useIntl();

	const onCancel = () => {
		props.onExit();
		const defaultStage = props.startingStage || DEFAULT_CREATE_STAGE;
		setFlowState(defaultStage);
	};

	const [errorFlags, setErrorFlags] = useState<Array<string>>([]);

	const addFlag = useCallback(
		(err: string) => {
			const newFlags = errorFlags.slice();

			newFlags.splice(0, 0, err);

			setErrorFlags(newFlags);
		},

		[errorFlags],
	);

	const handleDismiss = () => {
		setErrorFlags(errorFlags.slice(1));
	};

	useIntl();

	return (
		<Drawer
			testId="applinks-administration.ui.list.creation-flow-dialog.drawer"
			width="full"
			onClose={() => onCancel()}
			onCloseComplete={props.onCloseComplete}
			isOpen={props.isOpen}
		>
			<DrawerContent>
				<StateMachine
					state={flowState}
					onStateChange={setFlowState}
					onCancel={onCancel}
					onDisplayError={addFlag}
				/>
			</DrawerContent>
			<FlagGroup onDismissed={handleDismiss}>
				{errorFlags.map((err, idx) => {
					return (
						<ErrorFlag
							id={`flag-${idx}`}
							key={`flag-${idx}`}
							title={formatMessage(messages.errorFlagTitle)}
							description={err}
						/>
					);
				})}
			</FlagGroup>
		</Drawer>
	);
};
